import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { adminApi, type AdminAuditEvent } from '@/lib/api';
import { formatDate, EmptyState, TableSkeleton } from './AdminShared';

const ACTION_LABELS: Record<string, string> = {
  'workspace.update': 'Updated workspace',
  'workspace.delete': 'Deleted workspace',
  'workspace.trend_config.update': 'Updated trend config',
  'user.grant_superadmin': 'Granted superadmin',
  'user.revoke_superadmin': 'Revoked superadmin',
};

function summarizeMetadata(ev: AdminAuditEvent): string {
  if (!ev.metadata) return '';
  try {
    const meta = JSON.parse(ev.metadata) as Record<string, unknown>;
    if (Array.isArray(meta.fields)) return `fields: ${(meta.fields as string[]).join(', ')}`;
    if (typeof meta.name === 'string') return `name: ${meta.name}`;
    const keys = Object.keys(meta);
    return keys.length ? keys.join(', ') : '';
  } catch {
    return '';
  }
}

export function AdminAuditLog() {
  const { getToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.auditLog(token!, { limit: 200 });
    },
  });

  const events = data?.events ?? [];

  if (isLoading) return <TableSkeleton rows={8} />;
  if (events.length === 0) return <EmptyState message="No admin actions logged yet" />;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{events.length} most recent privileged actions</p>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-muted-foreground text-left">
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Actor</th>
              <th className="px-4 py-2.5 font-medium">Action</th>
              <th className="px-4 py-2.5 font-medium">Target</th>
              <th className="px-4 py-2.5 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{formatDate(ev.created_at)}</td>
                <td className="px-4 py-2.5 text-foreground">{ev.actor_email ?? '—'}</td>
                <td className="px-4 py-2.5 text-foreground">{ACTION_LABELS[ev.action] ?? ev.action}</td>
                <td className="px-4 py-2.5 text-muted-foreground">
                  {ev.target_type ? (
                    <span>
                      {ev.target_type}
                      {ev.target_id && (
                        <code className="ml-1 text-[10px]">{ev.target_id.slice(0, 8)}…</code>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[16rem]">{summarizeMetadata(ev)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
